import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';

/**
 * Un titre qui tient en `maxLines` lignes (`[vwFitLines]`) : la taille descend
 * d'un cran à l'autre (`steps`, en px pour un texte à 100 %, posés en rem et
 * multipliés par la taille réglée dans le téléphone) jusqu'à tenir. Si le plus petit cran ne
 * suffit pas (allemand, nom de variété, texte agrandi, petit écran), la
 * directive le signale : `overflowChange` émet vrai et `overflow()` passe à
 * vrai. C'est au parent de prendre la forme courte du texte.
 *
 * Le calcul se refait quand la largeur change (rotation, texte agrandi) ou
 * quand le texte change. Avec la forme courte, `overflow` peut repasser à
 * faux : le parent garde la forme courte jusqu'au message suivant, sinon les
 * deux formes alterneraient.
 *
 * L'élément porte son interligne sans unité (`leading-tight`) : il suit la
 * taille de chaque cran.
 * Usage : `<h1 [vwFitLines]="3" [steps]="[26, 24, 22]" (overflowChange)="short = short || $event">…</h1>`
 */
@Directive({
  selector: '[vwFitLines]',
  standalone: true,
  exportAs: 'vwFitLines',
})
export class FitLinesDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Nombre de lignes au plus. */
  @Input('vwFitLines') maxLines = 3;
  /** Tailles essayées dans l'ordre, en px à 100 % ; la première est la taille normale. */
  @Input() steps: number[] = [26, 24, 22];
  /** Vrai quand même le dernier cran dépasse `maxLines` : prendre la forme courte. */
  @Output() overflowChange = new EventEmitter<boolean>();

  /** Le texte dépasse au plus petit cran. */
  readonly overflow = signal(false);
  /** Le cran retenu, en px à 100 %. */
  readonly fontStep = signal<number | null>(null);

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private lastWidth = -1;
  private ready = false;

  ngAfterViewInit(): void {
    this.ready = true;
    const el = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      // Seule la largeur compte : la hauteur change à chaque cran essayé.
      this.resizeObserver = new ResizeObserver(() => {
        if (el.clientWidth !== this.lastWidth) this.fit();
      });
      this.resizeObserver.observe(el);
      this.mutationObserver = new MutationObserver(() => this.fit());
      this.mutationObserver.observe(el, { childList: true, characterData: true, subtree: true });
    });
    this.fit();
    // La police d'affichage (Jost) arrive parfois après le premier rendu.
    void document.fonts?.ready.then(() => this.fit());
  }

  ngOnChanges(): void {
    if (this.ready) this.fit();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  /** Essaie chaque cran du plus grand au plus petit ; garde le premier qui tient. */
  fit(): void {
    const el = this.host.nativeElement;
    this.lastWidth = el.clientWidth;
    const steps = this.steps?.length ? this.steps : [parseFloat(getComputedStyle(el).fontSize)];
    let chosen = steps[steps.length - 1];
    let overflow = true;
    for (const step of steps) {
      // En rem : le titre suit aussi la taille de texte du navigateur et du téléphone.
      el.style.fontSize = `calc(${step / 16}rem * var(--vw-text-k, 1))`;
      if (this.lineCount(el) <= this.maxLines) {
        chosen = step;
        overflow = false;
        break;
      }
    }
    this.fontStep.set(chosen);
    if (overflow !== this.overflow()) {
      this.zone.run(() => {
        this.overflow.set(overflow);
        this.overflowChange.emit(overflow);
        this.cdr.markForCheck();
      });
    }
  }

  /** Lignes occupées : la hauteur du contenu divisée par l'interligne. */
  private lineCount(el: HTMLElement): number {
    const style = getComputedStyle(el);
    const fontSize = parseFloat(style.fontSize);
    const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.2;
    const height = el.scrollHeight - parseFloat(style.paddingTop || '0') - parseFloat(style.paddingBottom || '0');
    return Math.round(height / lineHeight);
  }
}
